import { Check, ChevronDown } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface Craftspace {
  id: string;
  name: string;
  icon_url?: string | null;
}

interface CraftspaceSelectorProps {
  craftspaces: Craftspace[];
  selectedCraftspaceId?: string;
  onCraftspaceSelect: (craftspaceId: string) => void;
  placeholder?: string;
  disabled?: boolean;
  excludeCraftspaceId?: string;
}

const getCraftspaceInitials = (name: string): string => {
  if (!name) return "WS";

  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }

  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

export const CraftspaceSelector: React.FC<CraftspaceSelectorProps> = ({
  craftspaces,
  selectedCraftspaceId,
  onCraftspaceSelect,
  placeholder = "Select a craftspace...",
  disabled = false,
  excludeCraftspaceId,
}) => {
  const [open, setOpen] = useState(false);

  const filteredCraftspaces = craftspaces.filter(
    (craftspace) => !excludeCraftspaceId || craftspace.id !== excludeCraftspaceId,
  );

  const selectedCraftspace = craftspaces.find((w) => w.id === selectedCraftspaceId);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
          disabled={disabled}
        >
          {selectedCraftspace ? (
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 flex items-center justify-center rounded-sm bg-gray-600 border border-gray-500">
                {selectedCraftspace.icon_url ? (
                  <img
                    src={selectedCraftspace.icon_url}
                    alt={`${selectedCraftspace.name} icon`}
                    className="w-4 h-4 rounded-sm object-cover"
                    onError={(e) => {
                      // Hide the image and let the fallback show
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : null}
                {!selectedCraftspace.icon_url && (
                  <span className="text-xs font-semibold text-white">
                    {getCraftspaceInitials(selectedCraftspace.name)}
                  </span>
                )}
              </div>
              <span>{selectedCraftspace.name}</span>
            </div>
          ) : (
            placeholder
          )}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0 bg-gray-900 border-gray-700">
        <Command className="bg-gray-900">
          <CommandInput
            placeholder="Search craftspaces..."
            className="text-white bg-gray-800 border-gray-600 w-full [&>div>svg]:hidden"
          />
          <CommandList className="bg-gray-900">
            <CommandEmpty className="text-gray-400">No craftspace found.</CommandEmpty>
            <CommandGroup className="bg-gray-900">
              {filteredCraftspaces.map((craftspace) => (
                <CommandItem
                  key={craftspace.id}
                  value={craftspace.name}
                  onSelect={() => {
                    onCraftspaceSelect(craftspace.id);
                    setOpen(false);
                  }}
                  className="flex items-center space-x-2 cursor-pointer bg-gray-900 text-white hover:bg-gray-700 data-[selected=true]:bg-gray-700 data-[selected=true]:text-white"
                >
                  <div className="w-6 h-6 flex items-center justify-center rounded-sm bg-gray-600 border border-gray-500">
                    {craftspace.icon_url ? (
                      <img
                        src={craftspace.icon_url}
                        alt={`${craftspace.name} icon`}
                        className="w-4 h-4 rounded-sm object-cover"
                        onError={(e) => {
                          // Hide the image and let the fallback show
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : null}
                    {!craftspace.icon_url && (
                      <span className="text-xs font-semibold text-white">
                        {getCraftspaceInitials(craftspace.name)}
                      </span>
                    )}
                  </div>
                  <span className="text-white">{craftspace.name}</span>
                  {selectedCraftspaceId === craftspace.id && (
                    <Check className="ml-auto h-4 w-4 text-blue-400" />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
